import { create } from 'zustand'
import { useNotificationStore } from '@/stores/notification-store'

/**
 * App-wide language option: the single source of truth for what language the
 * AI (Clicky + the analyst + TTS) responds in. The AI always follows it,
 * whether it was picked in the + menu, on the profile page, by `set_language`,
 * or the English default when nothing was ever picked. The browser locale is
 * NOT used: a Spanish browser made English questions come back in Spanish
 * (2026-10-20); Spanish speakers are flipped by the detector instead.
 *
 * One automatic change exists: when a typed or spoken question is clearly
 * Spanish (features/companion/language-detect.ts) the option flips to Spanish
 * BEFORE that turn is sent, persists like any pick, and a one-line notice says
 * where to change it back. English is never detected; going back to English is
 * the trader's pick.
 *
 * Read from: the + menu, the profile page, the manager (Clicky prompt), the
 * ask_analyst tool executor, the direct analyst route and TTS. A `set_language`
 * tool lets the user change it conversationally ("switch to Spanish").
 *
 * Persisted (a genuine stable preference, unlike Ask/Do mode which resets each
 * session). Can later be sourced from the account profile for cross-device sync.
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English', name: 'English', bcp47: 'en-US' },
  { code: 'es', label: 'Español', name: 'Spanish', bcp47: 'es-ES' },
] as const

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code']

const LANG_KEY = 'edge:clicky:language'
const DEFAULT_LANG: LanguageCode = 'en'

function isSupported(code: string): code is LanguageCode {
  return SUPPORTED_LANGUAGES.some((l) => l.code === code)
}

/** Only `setLanguage` and `adoptDetectedLanguage` write LANG_KEY; the default is never persisted. */
function readPersisted(): LanguageCode {
  try {
    const stored = localStorage.getItem(LANG_KEY)
    if (stored && isSupported(stored)) return stored
  } catch {
    /* ignore */
  }
  return DEFAULT_LANG
}

function persist(code: LanguageCode): void {
  try {
    localStorage.setItem(LANG_KEY, code)
  } catch {
    /* ignore quota / unavailable storage */
  }
}

/** English name of a language code, for prompt instructions ("respond in Spanish"). */
export function languageName(code: LanguageCode): string {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.name ?? 'English'
}

/** BCP-47 tag for a code, for TTS voice selection. */
export function languageBcp47(code: LanguageCode): string {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.bcp47 ?? 'en-US'
}

/** Shown (in Spanish, the language the trader just wrote) when a Spanish question flips the option. */
export const SPANISH_FLIP_NOTICE =
  'Respondo en español. Puedes cambiar el idioma en el menú +, opción Language.'
const NOTICE_MS = 8000
/** The toast's key: a later pick (menu, profile, set_language) takes it down. */
const NOTICE_KEY = 'language-flip'

function clearFlipNotice(): void {
  const notifications = useNotificationStore.getState()
  if (notifications.current?.key === NOTICE_KEY) notifications.clearNotification()
}

interface LanguageStoreState {
  language: LanguageCode
  setLanguage: (code: LanguageCode) => void
  /** The automatic flip: persists like a pick and raises the notice toast. */
  adoptDetectedLanguage: (code: LanguageCode) => void
}

/** The English language name the AI answers in: always the option, on every request's `language` field. */
export function forcedLanguageName(): string {
  return languageName(useLanguageStore.getState().language)
}

export const useLanguageStore = create<LanguageStoreState>((set) => ({
  language: readPersisted(),
  setLanguage: (code) => {
    persist(code)
    clearFlipNotice()
    set({ language: code })
  },
  adoptDetectedLanguage: (code) => {
    persist(code)
    set({ language: code })
    // The app's one toast, so the flip is seen on any surface (chat, inline
    // composer, push-to-talk with the chat closed).
    useNotificationStore.getState().showNotification({
      key: NOTICE_KEY,
      message: SPANISH_FLIP_NOTICE,
      variant: 'info',
      autoHideDuration: NOTICE_MS,
    })
  },
}))
